import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { api, formatApiError, formatDate } from "@/lib/api";

export default function HistoryPage() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("tutti");
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/dispatches", { params: { q, status: status === "tutti" ? "" : status } })
      .then((r) => setList(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [q, status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const resend = async (d) => {
    setBusy(d.id);
    try {
      const { data } = await api.post(`/dispatches/${d.id}/resend`);
      if (data.failed) toast.error(data.errors[0] || "Invio non riuscito");
      else toast.success(`Busta paga reinviata a ${d.email}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  return (
    <>
      <PageHeader eyebrow="Registro" title="Storico invii" description="Ogni email spedita, con esito e possibilità di reinvio." />
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative w-full sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca dipendente, email o periodo" className="pl-9" data-testid="history-search-input" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-44" data-testid="history-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti gli esiti</SelectItem>
            <SelectItem value="inviato">Inviati</SelectItem>
            <SelectItem value="fallito">Falliti</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="panel overflow-x-auto" data-testid="history-table">
        <Table className="min-w-[860px]">
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead><TableHead>Dipendente</TableHead><TableHead>Periodo</TableHead>
              <TableHead>File</TableHead><TableHead>Esito</TableHead><TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((d) => (
              <TableRow key={d.id} data-testid={`dispatch-row-${d.id}`} className="hover:bg-slate-50">
                <TableCell className="whitespace-nowrap text-sm text-slate-600">{formatDate(d.sent_at)}</TableCell>
                <TableCell>
                  <p className="font-medium text-slate-900">{d.employee_name}</p>
                  <p className="text-xs text-slate-500">{d.email}</p>
                </TableCell>
                <TableCell className="text-sm">{d.period || "—"}</TableCell>
                <TableCell className="max-w-[220px] truncate font-mono text-xs text-slate-500">{d.filename}</TableCell>
                <TableCell>
                  <StatusBadge status={d.status} testId={`dispatch-status-${d.id}`} />
                  {d.error && <p className="mt-1 max-w-[260px] text-xs text-red-600">{d.error}</p>}
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" disabled={busy === d.id} onClick={() => resend(d)} data-testid={`resend-dispatch-${d.id}`}>
                    <RotateCw className={`mr-1 h-3.5 w-3.5 ${busy === d.id ? "animate-spin" : ""}`} /> Reinvia
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {list.length === 0 && (
              <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-slate-500" data-testid="history-empty">Nessun invio registrato.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
